/**
 * Which speakers the training's sound comes out of while presenting - pure,
 * so the rules are tested without a browser. The trainer picks an audio
 * output; the player that plays the recording applies it with
 * `HTMLMediaElement.setSinkId`.
 */

/** An audio output of this computer (`audiooutput` in `enumerateDevices`). */
export type AudioOutput = {
	/** Empty while the browser has not been given audio access. */
	deviceId: string
	/** Empty while the browser has not been given audio access. */
	label: string
}

/** What `setSinkId` takes for the system's own default output. */
export const DEFAULT_SINK = ''

/** The `audiooutput` devices of what `enumerateDevices` returned. */
export const audioOutputsOf = (
	devices: ReadonlyArray<Pick<MediaDeviceInfo, 'kind' | 'deviceId' | 'label'>>
): AudioOutput[] =>
	devices
		.filter(device => device.kind === 'audiooutput')
		.map(({ deviceId, label }) => ({ deviceId, label }))

/**
 * Until the browser is given audio access it hides the devices' names and
 * ids: asking for access reveals them, and a list without them says nothing
 * of what is plugged in.
 */
export const namesHidden = (outputs: ReadonlyArray<AudioOutput>): boolean =>
	outputs.some(output => output.deviceId === '' || output.label === '')

export type SinkResolution = {
	/** What to give `setSinkId`: the chosen device, or `DEFAULT_SINK`. */
	sinkId: string
	/** A device was chosen but cannot be used: the sound plays on the default. */
	unavailable: boolean
}

type ResolveInput = {
	/** The trainer's choice; `null` is the system default. */
	chosen: string | null
	/** What is plugged in now; `null` until the browser has listed it. */
	outputs: ReadonlyArray<AudioOutput> | null
	/** The device the browser refused (`NotFoundError`, `NotAllowedError`). */
	refused: string | null
}

/**
 * Where the sound goes: the chosen device while it is there, the default
 * once it is gone (unplugged) or refused. A list that cannot be read yet, or
 * that the browser has redacted, is no proof of absence - the browser's
 * answer to `setSinkId` decides then.
 */
export const resolveSink = ({
	chosen,
	outputs,
	refused,
}: ResolveInput): SinkResolution => {
	if (chosen === null) {
		return { sinkId: DEFAULT_SINK, unavailable: false }
	}
	const listed = outputs !== null && outputs.length > 0 && !namesHidden(outputs)
	const gone = listed && !outputs.some(output => output.deviceId === chosen)
	return gone || refused === chosen
		? { sinkId: DEFAULT_SINK, unavailable: true }
		: { sinkId: chosen, unavailable: false }
}

export type SpeakersView =
	/** The browser cannot choose an output: the sound plays on the default. */
	| { kind: 'unsupported' }
	| {
			kind: 'choose'
			/** The outputs to choose from, by name; the default is always there. */
			outputs: AudioOutput[]
			/** What the select shows: `DEFAULT_SINK` or one of `outputs`. */
			selected: string
			/** Asking the browser for audio access would reveal the names. */
			namesHidden: boolean
			/** The chosen speakers cannot be used; the default plays instead. */
			unavailable: boolean
			/** A non-default output is chosen: YouTube still plays on the default. */
			youtubeNote: boolean
	  }

type ViewInput = {
	supported: boolean
	chosen: string | null
	outputs: ReadonlyArray<AudioOutput> | null
	/** The presentation window could not play through the chosen speakers. */
	projectorFellBack: boolean
}

/** What the picker in the presenter panel shows. */
export const speakersView = ({
	supported,
	chosen,
	outputs,
	projectorFellBack,
}: ViewInput): SpeakersView => {
	if (!supported) {
		return { kind: 'unsupported' }
	}
	const known = outputs ?? []
	const named = known.filter(output => output.deviceId && output.label)
	const unavailable =
		resolveSink({ chosen, outputs, refused: null }).unavailable ||
		(chosen !== null && projectorFellBack)
	return {
		kind: 'choose',
		outputs: named,
		selected:
			chosen !== null &&
			!unavailable &&
			named.some(output => output.deviceId === chosen)
				? chosen
				: DEFAULT_SINK,
		namesHidden: namesHidden(known),
		unavailable,
		youtubeNote: chosen !== null,
	}
}

/**
 * Whether the trainer has to be told even with the picker closed: the chosen
 * speakers cannot be used, so the sound is not where they think it is.
 */
export const speakersNeedAttention = (view: SpeakersView): boolean =>
	view.kind === 'choose' && view.unavailable
